/**
 * Plain-English labels for billing values the API returns as codes
 * (PARTIALLY_PAID, bank_transfer, ClassroomSection, ...).
 *
 * Most bursars, parents and students are not technical, so a raw enum on a tag
 * or in a table reads as an error. Lookups go through normalizeCode because the API, the generated enums and
 * older filters spell the same status three ways (see normalizeCode).
 * Unknown values fall back to a humanised version instead of the raw code.
 */

/**
 * One spelling for a code, whichever form it arrives in. Responses carry the
 * backend's PHP enum CASE NAME ('PartiallyPaid', 'Successful'), the generated
 * TS enums hold the GraphQL name ('PARTIALLY_PAID'), and older code and
 * array-built resolvers use the database value ('partially_paid'). All three
 * become 'partially_paid'.
 */
export function normalizeCode(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

/**
 * Compare an API status with an expected one without caring about the form:
 * isCode(payment.status, PaymentStatusEnum.Successful). Never compare raw
 * status strings — 'Successful' !== 'SUCCESSFUL' !== 'successful'.
 */
export function isCode(value: string | null | undefined, expected: string): boolean {
  return normalizeCode(value) !== '' && normalizeCode(value) === normalizeCode(expected);
}

/** 'PARTIALLY_PAID' / 'bank_transfer' -> 'Partially paid' / 'Bank transfer'. */
export function humanizeCode(value: string | null | undefined): string {
  if (!value) return '';
  const words = value.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function lookup(map: Record<string, string>, value: string | null | undefined): string {
  if (!value) return '';
  return map[normalizeCode(value)] ?? humanizeCode(value);
}

/** Who is reading: staff manage bills; payers (students, parents) owe them. */
export type BillAudience = 'staff' | 'payer';

const BILL_STATUS_STAFF: Record<string, string> = {
  draft: 'Draft (not sent)',
  published: 'Published',
  partially_paid: 'Part paid',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Cancelled',
};

const BILL_STATUS_PAYER: Record<string, string> = {
  ...BILL_STATUS_STAFF,
  published: 'Not paid',
};

export function billStatusLabel(status: string | null | undefined, audience: BillAudience = 'staff'): string {
  return lookup(audience === 'payer' ? BILL_STATUS_PAYER : BILL_STATUS_STAFF, status);
}

const PAYMENT_STATUS: Record<string, string> = {
  successful: 'Successful',
  pending: 'Awaiting confirmation',
  failed: 'Didn\'t go through',
  voided: 'Cancelled',
  refunded: 'Refunded',
  partially_refunded: 'Partly refunded',
};

export function paymentStatusLabel(status: string | null | undefined): string {
  return lookup(PAYMENT_STATUS, status);
}

const PAYMENT_METHOD: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  transfer: 'Bank transfer',
  card: 'Card',
  cheque: 'Cheque',
  pos: 'POS',
  wallet: 'Wallet credit',
  paystack: 'Paystack (online)',
  interswitch: 'Interswitch (online)',
  remita: 'Remita (online)',
  flutterwave: 'Flutterwave (online)',
  monnify: 'Monnify (online)',
};

export function paymentMethodLabel(method: string | null | undefined): string {
  return lookup(PAYMENT_METHOD, method);
}

const APPROVAL_STATUS: Record<string, string> = {
  pending: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Declined',
  revoked: 'Withdrawn',
  processed: 'Paid out',
  failed: 'Failed',
};

/** Exemption and refund requests share the same approval flow. */
export function approvalStatusLabel(status: string | null | undefined): string {
  return lookup(APPROVAL_STATUS, status);
}

const DISCOUNT_TYPE: Record<string, string> = {
  percentage: 'Percentage',
  fixed: 'Fixed amount',
};

export function discountTypeLabel(type: string | null | undefined): string {
  return lookup(DISCOUNT_TYPE, type);
}

const DISCOUNT_SOURCE: Record<string, string> = {
  manual: 'Manual discount',
  rule: 'Automatic discount',
  exemption: 'Exemption',
};

export function discountSourceLabel(source: string | null | undefined): string {
  return lookup(DISCOUNT_SOURCE, source);
}

const LEDGER_ENTRY: Record<string, string> = {
  bill: 'Fee charged',
  payment: 'Payment',
  exemption: 'Exemption',
  discount: 'Discount',
  refund: 'Refund',
};

export function ledgerEntryLabel(type: string | null | undefined): string {
  return lookup(LEDGER_ENTRY, type);
}

const BILLABLE_TYPE: Record<string, string> = {
  classroom: 'Whole class',
  classroom_section: 'Class section',
  student: 'Individual student',
  programme: 'Programme',
  form_submission: 'Application fee',
  transcript_request: 'Transcript request',
};

/** Accepts the short ('ClassroomSection') or class-path ('App\\Models\\ClassroomSection') form. */
export function billableTypeName(type: string | null | undefined): string {
  if (!type) return '';
  const short = type.split('\\').pop() ?? type;
  return lookup(BILLABLE_TYPE, short);
}
